// Feel tuning for the skate sim: arcade numbers, not physics. Tune here.
// Speeds are m/s, times seconds, angles radians unless a name says otherwise.

export const G = 18; // heavier than real gravity: snappier, more "video game" air
// Ramps pull on you much less than the air does. That is the THPS cheat that
// makes a 3m vert wall easy to ride up while airs still feel snappy.
export const RAMP_G = G * 0.35;
/**
 * Extra lift off a vert lip, the way THPS throws you well above the coping.
 * Tuned with CRUISE: a cruise-speed air rises ~1.8m, so specials like the 900 need a crouch.
 */
export const VERT_POP = 1.13;
/** Caps vert launch speed so a full crouch gives ~3m of air, not a moon shot. */
export const MAX_VERT_LAUNCH = 10.5;
export const OLLIE_V = 6;
export const OLLIE_CHARGE_BONUS = 2.2;
export const FULL_CHARGE = 0.45;
// Like THPS the skater always pushes: no input rolls you at CRUISE, crouching
// (holding ollie) tucks you up toward CROUCH_SPEED, down brakes. Up does nothing.
export const CRUISE = 9.5;
export const AUTO_PUSH = 6.25;
export const CROUCH_SPEED = 15;
export const CROUCH_ACCEL = 4.25;
export const BRAKE = 12;
export const ROLL_FRICTION = 0.3;
export const MAX_SPEED = 19.5;
export const TURN_RATE = 3.1;
/** How fast a water slide's chute turns you to follow it (rad/s, #163): enough for its tightest bend at full speed. */
export const CHUTE_STEER = 8;
export const AIR_SPIN_RATE = 6.6; // ~380 deg/s
export const STEP_UP = 0.25;
export const DROP = 0.25;
/** Furthest off the line you're moving along a landing can be before it bails. */
export const LAND_ANGLE = (50 * Math.PI) / 180;
export const LAND_COS = Math.cos(LAND_ANGLE);
/** Landing more than this far off the line you're moving along is kept, but Sloppy. */
export const SLOPPY_ANGLE = (30 * Math.PI) / 180;
/** A spin (180 or more) landed within this of straight is Perfect. */
export const PERFECT_ANGLE = (8 * Math.PI) / 180;
/** What a Sloppy landing keeps of the combo, and what a Perfect one adds. */
export const SLOPPY_KEEP = 0.75;
export const PERFECT_BONUS = 1.1;
/**
 * A light vert assist (#183, was 85 degrees at 5 rad/s with no limit, which
 * landed nearly every air dead straight): a vert air within VERT_ALIGN_TOL
 * of the ramp's line, with the stick let go, is eased toward it at
 * VERT_ALIGN_RATE, but by no more than VERT_ALIGN_MAX in one air. A small
 * drift comes down straight; a crooked air stays crooked (Sloppy past
 * SLOPPY_ANGLE, a bail past LAND_ANGLE).
 */
export const VERT_ALIGN_TOL = (40 * Math.PI) / 180;
export const VERT_ALIGN_RATE = 1.5; // rad/s
export const VERT_ALIGN_MAX = (20 * Math.PI) / 180;
/** How fast (rad/s) a bowl corner's carve is given back (`AirState.vertCarve`): the old full-strength assist. */
export const VERT_CARVE_RATE = 5;
/**
 * Ollie on the vert face (THPS): released while riding up a quarter pipe
 * steeper than VERT_OLLIE_MIN_ANGLE, it waits for the launch line and makes
 * the vert air higher instead of popping you off the wall. Extra height is
 * VERT_OLLIE_HEIGHT, plus up to VERT_OLLIE_CHARGE_HEIGHT with a full crouch
 * charge, still capped by MAX_VERT_LAUNCH.
 */
export const VERT_OLLIE_MIN_ANGLE = (35 * Math.PI) / 180;
export const VERT_OLLIE_HEIGHT = 0.15;
export const VERT_OLLIE_CHARGE_HEIGHT = 0.1;
/** Still crouched at the launch line: letting go this soon after still counts as the ollie. */
export const VERT_OLLIE_LATE = 0.12;
/** How fast the board eases flat in the air once it's no longer a vert air (1/s). */
export const AIR_FLATTEN_RATE = 8;
export const BAIL_TIME = 1.6;
export const GRIND_SNAP_DIST = 0.8;
export const GRIND_BUFFER = 0.3;
export const GRIND_MIN_SPEED = 5;
/** A coaster's chain lift (#189): how fast it carries a grind up the lift hill (m/s). */
export const LIFT_SPEED = 7;
/** How hard a sloped rail (a handrail, #10) pulls you along it, per unit of slope: the same as a ramp. */
export const GRIND_SLOPE_G = RAMP_G;
/** Share of your speed carried onto a rail however steep the angle you hit it at. */
export const GRIND_KEEP = 0.95;
/**
 * Catching a coping from the air keeps the whole speed you had (up the wall
 * too, not just across it) plus this much, so a fast carve into the lip makes
 * a fast coping grind (#133).
 */
export const COPING_BOOST = 1;
export const GRIND_COOLDOWN = 0.3;
/**
 * An ollie pressed in the air this long (s) before locking onto a rail is
 * kept (#244): rising onto a short ledge is exactly when you press it. It
 * pops out once the grind has lasted `GRIND_POP_HOLD` (s), long enough to
 * count; pressed any earlier it's ignored, so it never becomes a surprise pop.
 */
export const GRIND_POP_BUFFER = 0.15;
export const GRIND_POP_HOLD = 0.04;
/**
 * Side hop off a grind (#232, L1 / R1 on a rail): how hard it pops (m/s up,
 * lower than an ollie out) and how fast it carries you sideways, which puts
 * you down about a metre and a half beside a waist-high rail.
 */
export const SIDE_HOP_V = 4.5;
export const SIDE_HOP_OUT = 2.6;
/**
 * The stick held left or right as you ollie out of a grind pushes you that
 * way, up to this fast (m/s) at full stick; pushes under `GRIND_OUT_DEAD`
 * are the needle being balanced, not a steer.
 */
export const GRIND_OUT_STEER = 1.8;
export const GRIND_OUT_DEAD = 0.5;
/** A grind switch kicks the balance needle this hard, the way it was already leaning. */
export const GRIND_SWITCH_NUDGE = 0.35;
/** After touching down on a ramp you have this long to revert (R2) before the combo banks. */
export const REVERT_WINDOW = 0.3;
/** R2 pressed this long before touchdown still counts as a revert. */
export const REVERT_BUFFER = 0.25;
export const REVERT_POINTS = 100;
export const NOLLIE_POINTS = 100;
/** A plain ollie popped while rolling fakie scores as a "Fakie Ollie". */
export const FAKIE_OLLIE_POINTS = 50;
/** Switch is harder: tricks score a bit more, flips take a bit longer, balance is twitchier. */
export const SWITCH_BONUS = 1.2;
export const SWITCH_FLIP_TIME = 1.1;
export const SWITCH_BALANCE = 1.15;
/**
 * R2 this soon after the revert window closes is a revert pressed a little
 * late, not a stance switch: it does nothing (#134).
 */
export const STANCE_LATE_REVERT = 0.4;
/**
 * Leaving the ground this soon after an R2 on flat means it was meant for the
 * air (an acid drop or spine transfer): the stance switch is undone and the
 * press carries into the air for this long (#134).
 */
export const STANCE_TAKEOFF = 0.2;
/** How hard the stick pulls the balance needle (grinds, manuals, lips), scaled up with the combo's difficulty. */
export const BALANCE_STICK = 9;
/** How fast pushing against the needle's motion stops it (1/s at full stick). */
export const BALANCE_STICK_BRAKE = 4;
/** Speed straight into a wall (m/s) past which the hit is a bail rather than a bump or slide. */
export const WALL_BAIL_SPEED = 5.5;
/** Share of the along-the-wall speed kept when you glance off and slide. */
export const WALL_SLIDE_KEEP = 0.85;
/** Ollie pressed this long before hitting a wall in the air still plants. */
export const PLANT_BUFFER = 0.25;
/** After hitting a wall in the air you hang on it this long; ollie in that time plants. */
export const WALL_GRACE = 0.15;
/** How long a wallplant sticks to the wall before kicking off. */
export const PLANT_STICK = 0.12;
export const PLANT_POP = 6.5;
/** Kick-off speed straight out from the wall: a base push plus some of the hit. */
export const PLANT_OUT_BASE = 4;
export const PLANT_OUT_KEEP = 0.35;
export const PLANT_POINTS = 200;
/** Along-the-wall speed (m/s) a wallride needs; slower hits just hang on the wall. */
export const WALLRIDE_MIN_SPEED = 3;
/** Longest a wallride sticks before you drop off the wall. */
export const WALLRIDE_TIME = 1.5;
/** Gravity on the wall: it eases you down rather than dropping you. */
export const WALLRIDE_G = G * 0.25;
/** Vertical speed a wallride starts with, clamped to this range: a fall gets caught, a rise tamed. */
export const WALLRIDE_VY_MIN = 0.5;
export const WALLRIDE_VY_MAX = 2.5;
/** How far into the wall to look for it still being there. */
export const WALLRIDE_PROBE = 0.4;
/** The board lies against the wall with the rider leaning out: the "up" it rides on. */
export const WALLRIDE_TILT_UP = 0.7;
export const WALLRIDE_POINTS = 150;
export const WALLRIDE_PER_SECOND = 250;
/** A Wallie pops up and out from the wall, keeping most of the ride's speed. */
export const WALLIE_POP = 6;
export const WALLIE_OUT = 3;
export const WALLIE_KEEP = 0.9;
export const WALLIE_POINTS = 200;
/** A spine transfer comes down the far side about this steep (0 = flat, 90 = vertical). */
export const TRANSFER_LAND_ANGLE = (60 * Math.PI) / 180;
/** Horizontal speed over the coping on a transfer: at least enough to read as an arc, never a launch. */
export const TRANSFER_MIN_SPEED = 0.6;
export const TRANSFER_MAX_SPEED = 6;
export const TRANSFER_POINTS = 150;
/** How far ahead along the air an acid drop looks for a transition to drop into. */
export const ACID_REACH = 4;
/** Fastest an acid drop steers you along the ramp's line, so it bends the air rather than teleporting. */
export const ACID_MAX_SPEED = 8;
/** An acid drop needs at least this much fall onto the transition, or it's just a landing. */
export const ACID_MIN_FALL = 0.3;
export const ACID_POINTS = 150;
/**
 * Up on the stick past this as you launch off vert (#77): a transfer air
 * that carries out over the deck instead of coming back down the ramp.
 */
export const TRANSFER_OUT_STICK = 0.5;
/**
 * A transfer air keeps this share of the launch speed as speed out over the
 * deck (at most TRANSFER_OUT_MAX), from the moment it's clear of the coping
 * by TRANSFER_OUT_CLEAR. Tuned so a crouched full-speed launch clears a
 * 2-4 m gap and a cruise launch about 1.5 m.
 */
export const TRANSFER_OUT_KEEP = 0.3;
export const TRANSFER_OUT_MAX = 3.4;
export const TRANSFER_OUT_CLEAR = 0.1;
/**
 * Most a transfer air is nudged along the far ramp's line to come down its
 * transition, so it tidies a landing that was nearly there but never pulls
 * you across a gap you weren't going to make.
 */
export const TRANSFER_ASSIST = 1.2;
/**
 * Side transfers (#116): a vert launch carving at least this far off the
 * ramp's line along the coping (or with the stick held past SIDE_STICK that
 * way) toward a ramp beside it carries over and comes down that one.
 */
export const SIDE_MIN_ANGLE = (15 * Math.PI) / 180;
export const SIDE_STICK = 0.5;
/**
 * Most a side transfer adds to the carve's own speed along the coping
 * without the stick held that way, and the most it ever carries: enough for
 * the Sunny Beach halfpipe's 4 m hop to the big quarter, not a launch
 * across the park.
 */
export const SIDE_ASSIST = 2.5;
export const SIDE_MAX_SPEED = 6;
/** How far past the gap a side transfer aims to come down, onto the far ramp. */
export const SIDE_LAND_IN = 1;
/** Spacing of the look along the coping for the gap and the far ramp. */
export const SIDE_STEP = 0.25;
/**
 * A side transfer comes down partway up the far ramp's transition, no
 * steeper than this (0 = flat, 90 = vertical), drifting at most SIDE_SHIFT
 * along its line to get there when the two ramps don't start level.
 */
export const SIDE_LAND_ANGLE = (75 * Math.PI) / 180;
export const SIDE_SHIFT = 1.5;
export const SIDE_SHIFT_STEP = 0.1;
/** Landing down the far ramp after a transfer air. */
export const GAP_TRANSFER_POINTS = 250;
/** A lip trick needs you riding up the ramp within this angle of straight on; shallower hits grind the coping. */
export const LIP_MAX_ANGLE = (30 * Math.PI) / 180;
/** Dropping back in off the lip puts you on the ramp this steep, rolling down it at this speed. */
export const LIP_DROP_ANGLE = (80 * Math.PI) / 180;
export const LIP_DROP_SPEED = 3;
/** The coping is a harder balance than a rail. */
export const LIP_WOBBLE = 1.15;
/** A manual sequence (up, down) finished this long before touchdown still manuals on landing. */
export const MANUAL_BUFFER = 0.6;
/** A grab pressed mid-flip starts when the flip finishes if it's still held, or was let go at most this long before (s). */
export const GRAB_BUFFER = 0.25;
/**
 * A body flip (the bike's Backflip, #144) speeds up to come round in the air
 * that's left, down to this many seconds a whole flip; with less air than that
 * it still lands mid-flip and bails.
 */
export const BODY_FLIP_MIN_TIME = 0.6;
/** Seconds a sped-up body flip aims to finish before touching down, so the landing reads. */
export const BODY_FLIP_MARGIN = 0.05;
/** How far ahead (s) the arc is followed to find where an air comes down, and in what steps. */
export const LAND_PREDICT_MAX = 3;
export const LAND_PREDICT_DT = 1 / 60;
/**
 * Combo links (THPS 2): after a flat landing the combo stays open this long
 * for a manual before it banks, so up, down pressed just after touchdown
 * still links...
 */
export const LAND_LINK_WINDOW = 0.25;
/** ...and after a revert, this long for a manual (air, revert, manual). */
export const REVERT_LINK_WINDOW = 0.5;
/** After letting go of a special grab, this long to get back on the board; land sooner and you bail. */
export const SPECIAL_RECOVER = 0.25;
/** Kick a ground trick gives the manual's needle, toward the wheels that are down. */
export const POP_NUDGE = 0.5;
export const HOLD_NUDGE = 0.35;
/** Held ground tricks are harder to balance than the plain manual. */
export const HOLD_WOBBLE = 1.25;

/** How the last landing went, when it was worth calling out (THPS): clean landings have none. */

// Soft edges (#82): a beach instead of a wall.
/** How hard sand slows you (m/s²): from cruise (9.5 m/s) you stop in under 4 m. */
export const SAND_DECEL = 12;
/** Below this you've stopped in the sand and it starts turning you back (m/s). */
export const SAND_STOPPED = 0.5;
/** How fast the sand turns you toward the concrete once you've stopped (rad/s). */
export const SAND_TURN = 1.6;
/** Facing within this of the concrete (cosine, about 20 degrees), you trudge back out... */
export const SAND_FACING = 0.94;
/** ...at this speed (m/s), until the concrete's auto-push takes over. */
export const SAND_CRAWL = 1.5;

// Skitching (#37): holding onto the back of a moving vehicle.
/** Up on the stick past this, rolling up behind a vehicle, grabs on. */
export const SKITCH_STICK = 0.6;
/** You can grab on from this far behind the vehicle's back end (m)... */
export const SKITCH_REACH = 2.5;
/** ...and this far out to either side of its width (m)... */
export const SKITCH_SIDE = 0.3;
/** ...rolling within this of its heading (cosine, about 45 degrees) at this speed or faster (m/s). */
export const SKITCH_FACING = 0.7;
export const SKITCH_MIN_SPEED = 3;
/** Where you ride while towed: this far behind its back end (m), swaying this far per unit of balance. */
export const SKITCH_GAP = 1;
export const SKITCH_SWAY = 0.35;
/** The needle's pull out of a turn, per m/s² of the vehicle's cornering. */
export const SKITCH_CORNER = 0.15;
/** Its balance, against a rail's. */
export const SKITCH_WOBBLE = 1;
/** Letting go: this much faster than the vehicle (m/s), veering this far off its line (radians) so you clear it. */
export const SKITCH_BOOST = 5;
export const SKITCH_VEER = (25 * Math.PI) / 180;
/** After letting go, that vehicle can't knock you down for this long (s), while you pull past it. */
export const SKITCH_GRACE = 1;
/** A vehicle knocks you down if you're this close to its sides and under its roof (m). */
export const VEHICLE_HIT_MARGIN = 0.2;

// Off the board (#19): stepping off with both triggers and walking about.
/** L2 and R2 pressed within this of each other (s) is stepping off (or back on), not a nollie and a switch. */
export const STEP_OFF_CHORD = 0.2;
/** You can only step off where the ground's this flat (slope), outside a combo. */
export const STEP_OFF_SLOPE = 0.2;
/** Running pace with the stick all the way up (m/s); part way walks slower. */
export const FOOT_RUN = 5.5;
/** Backing up with the stick down (m/s). */
export const FOOT_BACK = 1.5;
/** How fast your pace changes on your feet (m/s²). */
export const FOOT_ACCEL = 14;
/** Turning on the spot and while walking (rad/s): quicker than on the board. */
export const FOOT_TURN = 3.6;
/** A jump off your feet (m/s up): about 0.7 m high, so with a step (`FOOT_STEP`) it gets you onto a box up to about 1 m. */
export const FOOT_JUMP = 5;
/** The tallest step you walk up without jumping (m)... */
export const FOOT_STEP = 0.3;
/** ...and the steepest slope (rise over run) you can walk up. */
export const FOOT_MAX_SLOPE = 1;
/** Walking off anything more than this far above the ground below is a fall (m). */
export const FOOT_DROP = 0.3;
/**
 * Metres per step on foot at a walk (`FOOT_BACK` or slower) and at a full run
 * (`FOOT_RUN`), longer in between as you speed up (#122): the walk cycle's
 * legs swing through one step per this, and a footstep sounds each (#19).
 */
export const FOOT_STEP_WALK = 0.8;
export const FOOT_STEP_RUN = 1.4;
/** Jumping at a ledge too high to hop onto (#19), you catch it if its top is within this of your feet (m): with a jump's peak, ledges up to about 2.1 m. */
export const FOOT_REACH = 1.4;
/** Hanging, your feet are this far below the ledge's top (m), or on the ground if that's higher. */
export const FOOT_HANG = 1.7;
/** Climbing up from the hang takes this long (s)... */
export const FOOT_CLIMB = 0.7;
/** ...and you end up this far in from the edge (m). */
export const FOOT_LEDGE_IN = 0.4;
/** Dropping the board and hopping on: you roll off at your running pace, or this at least (m/s). */
export const FOOT_BOARD_ON = 3;
/** Stepping off mid-combo (THPS Underground's run out, #19): the combo waits this long on foot for a Caveman or getting back on, then banks (s). */
export const RUN_OUT = 2.5;
/** A Caveman (#19): both triggers in a jump on foot drop the board under you, a trick that starts a combo. */
export const CAVEMAN_POINTS = 200;
/** Moon gravity cheat (#119): gravity in the air is this share of the usual, for floaty airs. */
export const MOON_GRAVITY = 0.45;
/** Slow-mo cheat (#119): the whole game runs at this share of real time. */
export const SLOWMO_SPEED = 0.5;

// The backpack on foot (#136) and its paintball gun.
/** A paintball leaves the barrel this fast (m/s)... */
export const PAINT_SPEED = 28;
/** ...tipped up this far over the way you face (rad), for the arc... */
export const PAINT_LOFT = 0.1;
/** ...from this high over your feet (m), the gun shouldered at eye level (#143, the drawn barrel's tip)... */
export const PAINT_MUZZLE_Y = 1.3;
/** ...and this far ahead of you (m). */
export const PAINT_MUZZLE_FWD = 0.7;
/** Paintballs fall at real gravity (m/s²), lighter than the skater's arcade `G`, so a shot carries about 25 m on the flat. */
export const PAINT_G = 9.8;
/** Seconds between shots. */
export const PAINT_COOLDOWN = 0.3;
/** A paintball that hasn't hit anything by now is gone (s). */
export const PAINT_LIFE = 3;
/** Auto-aim (#137) lofts a shot at most this steeply up or down (m/s). */
export const PAINT_AIM_VY = 8;

// The power washer (#151): a straight jet ahead and a little down while □ is held.
/** The jet leaves the wand's nozzle this high over your feet (m), held shouldered like the gun... */
export const WASH_Y = 1.25;
/** ...this far ahead of you (m)... */
export const WASH_FWD = 0.8;
/** ...tipped down this far (rad), so it hits the floor about 5 m out... */
export const WASH_PITCH = 0.28;
/** ...and reaches this far before it's spray (m). */
export const WASH_REACH = 8;
/** Paint within this far of where the jet hits gets washed (m)... */
export const WASH_RADIUS = 0.6;
/** ...and a splat under it for this long is gone (s). */
export const WASH_TIME = 0.6;
/** A graffiti tag (#151) the jet hits for this long, anywhere on it, is clean (s). */
export const TAG_WASH_TIME = 1.2;

// Rides (#141): what the BMX does differently from the board, as multipliers
// on what the stats scale (`RIDES` in `sim/rides.ts`). The board's are all 1.
/** A bunny hop pops higher than an ollie... */
export const BMX_POP = 1.15;
/** ...and goes a little higher off vert. */
export const BMX_AIR = 1.05;
/** The bike's heavier to throw round: spins are slower. */
export const BMX_SPIN = 0.88;
/** Pedalling's a touch quicker than pushing. */
export const BMX_SPEED = 1.04;
/** Two pegs on the rail: steadier than a board's trucks. */
export const BMX_RAIL = 0.85;
/** One wheel down is twitchier than a board's tail. */
export const BMX_MANUAL = 1.1;
// The scooter (#139): a low pop and quick spins.
/** A scooter hop pops lower than an ollie... */
export const SCOOTER_POP = 0.85;
/** ...and doesn't carry as high off vert. */
export const SCOOTER_AIR = 0.95;
/** It weighs next to nothing: spins come round quicker. */
export const SCOOTER_SPIN = 1.2;
/** Kicking along is a touch slower than pushing a board. */
export const SCOOTER_SPEED = 0.96;
/** A flat deck on the rail: a little steadier than trucks. */
export const SCOOTER_RAIL = 0.92;
/** A small back wheel: a manual's a little twitchier. */
export const SCOOTER_MANUAL = 1.08;
// Roller skates (#140): they jump like an ollie and roll like a board.
/** Nothing to carry round: spins are a little quicker... */
export const SKATES_SPIN = 1.1;
/** ...soul plates sit steady on a rail... */
export const SKATES_RAIL = 0.85;
/** ...and the heel wheels are a short base to balance on. */
export const SKATES_MANUAL = 1.15;

// Pushing things with the power washer (#151, last slice).
/** The jet pushes a light prop (a cone, a can) it passes through this hard (m/s²)... */
export const WASH_PUSH = 14;
/** ...up to this fast (m/s). */
export const WASH_PUSH_MAX = 4.5;
/** A knocked-over prop runs down a slope this hard per unit of gradient (m/s²)... */
export const PROP_GRAVITY = 9;
/** ...but one at rest stays put on anything flatter than this gradient. */
export const PROP_REST = 0.12;
/** A prop bouncing off a wall keeps this much of its speed. */
export const PROP_BOUNCE = 0.4;
/** Spraying while standing on a slope steeper than this gradient shoves you back... */
export const WASH_SHOVE_SLOPE = 0.08;
/** ...this fast (m/s), the way you're not facing. */
export const WASH_SHOVE = 0.6;

// Bomb the Hill (#193): the downhill mode's carve, speed wobbles and slide-outs.
/** The hill pulls you down it harder than a ramp does (a share of G per unit of grade), so it keeps building speed. */
export const HILL_G = G * 0.55;
/** Pushing gets you rolling off the top, up to this speed (m/s); after that it's all the hill. */
export const HILL_PUSH_SPEED = 5;
/** How fast the carve follows the stick (per second): a key held leans in over about a third of a second. */
export const CARVE_EASE = 4;
/** A full carve turns you this fast (rad/s)... */
export const CARVE_TURN = 2;
/** ...and scrubs this share of your speed a second, less for a lighter carve. */
export const CARVE_SCRUB = 0.35;
/** Carving harder than this... */
export const SLIDE_CARVE = 0.85;
/** ...faster than this (m/s) slides the wheels out... */
export const SLIDE_SPEED = 20;
/** ...which scrubs this much more speed (m/s²)... */
export const SLIDE_SCRUB = 6;
/** ...and builds toward a fall at this much per second per m/s over SLIDE_SPEED (a fall at 1). */
export const SLIP_RATE = 1;
/** Off the slide, the slip goes away this fast (per second). */
export const SLIP_CALM = 1.5;
/** Past this speed (m/s) the board starts to wobble... */
export const WOBBLE_SPEED = 22;
/** ...building toward a face plant at this much per second per m/s over (a plant at 1)... */
export const WOBBLE_RATE = 0.22;
/** ...less the harder you carve (a full carve takes this share off)... */
export const WOBBLE_CARVE_CALM = 0.7;
/** ...and settling this fast (per second) once you're back under it. */
export const WOBBLE_CALM = 0.6;
/** Safety cap on hill speed (m/s): the wobbles get you long before this. */
export const HILL_MAX_SPEED = 45;
/** Seconds after a run ends before you're back at the top. */
export const HILL_RESET = 2.5;
/** Running into something on the hill (#194) knocks you back off it at this share of your speed. */
export const HIT_BOUNCE = 0.15;
/** The run's over this far (m) short of a fixed hill's bottom wall. */
export const HILL_FINISH = 8;
/** Metres a close call with someone on a crosswalk (#196) adds to the run's distance. */
export const HILL_CLOSE_BONUS = 5;
/** The endless road (#195) moves back up once the rider's this far plus a chunk below the top (m). */
export const HILL_RECENTRE = 30;
